import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import HeroBlock from './components/Hero';
import MainWrapper from './components/MainWrapper';
import FormSection from './components/FormSection';
import TotalSection from './components/TotalSection';
import AddStopSections from './components/AddStopSections';
import AddedStopsSection from './components/AddedStopsSection';
import TotalBlockButtons from './components/TotalBlockButtons';

import * as path from './constans/api';
import { axiosConfig } from './utils/axiosConfig';

const Wrapper = styled(Box)(() => ({
    backgroundColor: '#F5F5F5',
}));
export function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === name + '=') {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

function App() {
    const currentTourSlug = window.location.pathname.split('/')[2];
    const [currentTourId, setCurrentTourId] = useState(null);
    const [total, setTotal] = useState({ adults: 1, children: 0, infants: 0 });
    const [tourDataInfo, setTourDataInfo] = useState(null);
    const [allRoutes, setAllRoutes] = useState(null);
    const [addedStopsArr, setAddedStopsArr] = useState([]);
    const [popularStopsArr, setPopularStopsArr] = useState([]);
    const [addedStopsId, setAddedStopsId] = useState([]);
    const [popularStopsId, setPopularStopsId] = useState([]);
    const [date, setDate] = useState(null);
    const [time, setTime] = useState('');
    const [pickUpLocation, setPickUpLocation] = useState('');
    const [modalIsOpen, setIsOpenModal] = useState(false);
    const [checkboxIsCheked, setCheckboxIsCheked] = useState(true);
    const [dropOffLocation, setDropOffLocation] = useState('');
    const [duration, setDuration] = useState('');
    const [pickupAutocomplete, setPickupAutocomplete] = useState('');
    const [dropOffAutocomplete, setDropOffAutocomplete] = useState('');
    const [addClicked, setAddClicked] = useState(false);
    const [locationNotEmpty, setLocationNotEmpty] = useState(null);
    const [dropOffNotEmpty, setDropOffNotEmpty] = useState(null);
    const [dateNotEmpty, setDateNotEmpty] = useState(null);
    const [timeNotEmpty, setTimeNotEmpty] = useState(null);
    const [mobileButtonsVisible, setMobileButtonsVisible] = useState(false);

    const handleOpenModal = useCallback(() => setIsOpenModal(true), []);

    const handleCloseModal = useCallback(() => setIsOpenModal(false), []);

    const removeStation = useCallback((id) => setAddedStopsArr((prev) => prev.filter((el) => el.id !== id)), []);

    function debounce(f, ms) {
        let isCooldown = false;
        return function () {
            if (isCooldown) return;

            f.apply(this, arguments);

            isCooldown = true;

            setTimeout(() => (isCooldown = false), ms);
        };
    }

    const addStation = useCallback((el) => setAddedStopsArr((prev) => [...prev, el]), []);

    useEffect(
        () =>
            moment.updateLocale('en', {
                week: {
                    dow: 1,
                },
            }),
        []
    );

    const getDurationWithInputs = useCallback(
        async (id, curentTourId, pickup, dropOff, isCheck) => {
            try {
                const response = await axiosConfig({
                    method: 'post',
                    url: `${path.TOUR_API_DURATION}`,
                    data: {
                        places_id: id,
                        tour_route_id: curentTourId,
                        pick_up: pickup,
                        drop_off: isCheck ? pickup : dropOff,
                    },
                    headers: {
                        'X-CSRFToken': getCookie('csrftoken'),
                    },
                });
                setDuration(response.data);
            } catch (err) {
                console.log(err);
            }
        },
        [checkboxIsCheked]
    );

    const getDurationWithoutInputs = useCallback(async (id, curentTourId) => {
        try {
            const response = await axiosConfig({
                method: 'post',
                url: `${path.TOUR_API_DURATION}`,
                data: {
                    places_id: id,
                    tour_route_id: curentTourId,
                },
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            setDuration(response.data);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const getRouteInfo = useCallback(async (id) => {
        try {
            const response = await axiosConfig({
                method: 'get',
                url: `${path.TOUR_API}${path.TOUR_ROUTE}${id}/`,
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            setTourDataInfo(response.data);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const getDefaultPlaces = useCallback(async (tourId) => {
        try {
            const response = await axiosConfig({
                method: 'post',
                url: `${path.TOUR_API_DEFAULT_PLACES}`,
                data: {
                    tour_route_id: tourId,
                },
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            setPopularStopsArr(response.data);
            setAddedStopsArr(response.data);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const getAllRoutes = useCallback(async () => {
        try {
            const response = await axiosConfig({
                method: 'get',
                url: `${path.TOUR_API}${path.TOUR_ROUTE}`,
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            setAllRoutes(response.data);
            const currentRoute = response.data.filter((el) => el.tour.slug === currentTourSlug);
            setCurrentTourId(currentRoute[0].tour.id);
        } catch (err) {
            console.log(err);
        }
    }, [currentTourSlug]);

    const getPickupAutocomplete = useCallback(async (place) => {
        try {
            const response = await axiosConfig({
                method: 'post',
                url: `tour-api/places-autocomplete/`,
                data: {
                    string: place,
                },
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            setPickupAutocomplete(response.data);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const getDropOffAutocomplete = useCallback(async (place) => {
        try {
            const response = await axiosConfig({
                method: 'post',
                url: `tour-api/places-autocomplete/`,
                data: {
                    string: place,
                },
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            setDropOffAutocomplete(response.data);
        } catch (err) {
            console.log(err);
        }
    }, []);

    const getFullDurationWithInputs = useMemo(() => {
        return debounce(getDurationWithInputs, 2000);
    }, []);

    const getFullDurationWithoutnputs = useMemo(() => {
        return debounce(getDurationWithoutInputs, 2000);
    }, []);

    useEffect(() => {
        if (pickUpLocation) {
            getPickupAutocomplete(pickUpLocation);
        }
    }, [pickUpLocation]);

    useEffect(() => {
        if (dropOffLocation) {
            getDropOffAutocomplete(dropOffLocation);
        }
    }, [dropOffLocation]);

    useEffect(() => {
        getAllRoutes();
    }, [getAllRoutes]);

    useEffect(() => {
        if (currentTourId) {
            getRouteInfo(currentTourId);
        }
    }, [currentTourId, getRouteInfo]);

    useEffect(() => {
        if (tourDataInfo) {
            getDefaultPlaces(tourDataInfo.id);
        }
    }, [getDefaultPlaces, tourDataInfo]);

    useEffect(() => {
        if (addedStopsArr) {
            setAddedStopsId(addedStopsArr.map((el) => el.id));
        }
    }, [addedStopsArr]);

    useEffect(() => {
        if (addedStopsId.length) {
            if (pickUpLocation.trim().length > 15) {
                getFullDurationWithInputs(
                    addedStopsId.join(','),
                    tourDataInfo.id,
                    pickUpLocation,
                    dropOffLocation,
                    checkboxIsCheked
                );
            } else {
                getFullDurationWithoutnputs(addedStopsId.join(','), tourDataInfo.id);
            }
        }
    }, [addedStopsId, pickUpLocation]);

    useEffect(() => {
        if (addedStopsId.length) {
            if (dropOffLocation.trim().length > 15) {
                getFullDurationWithInputs(
                    addedStopsId.join(','),
                    tourDataInfo.id,
                    pickUpLocation,
                    dropOffLocation,
                    checkboxIsCheked
                );
            } else {
                getFullDurationWithoutnputs(addedStopsId.join(','), tourDataInfo.id);
            }
        }
    }, [addedStopsId, dropOffLocation]);

    useEffect(() => {
        if (popularStopsArr) {
            setPopularStopsId(popularStopsArr.map((el) => el.id));
        }
    }, [popularStopsArr]);

    const isMobile = useMemo(() => {
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            //
            return true;
        } else {
            return false;
        }
    }, []);

    const [isAddesScript, setIsAddedScript] = useState(false);

    useEffect(() => {
      if (tourDataInfo && !isAddesScript) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'view_item',
          currency: 'EUR',
          value: tourDataInfo?.tour?.price,
          custom: 'yes',
          items: [
            {
              item_id: String(tourDataInfo?.tour?.id),
              item_name: tourDataInfo?.tour?.name,
              discount: 0,
              price: tourDataInfo?.tour?.price,
            },
          ],
        });
        setIsAddedScript(true);
      }
    }, [tourDataInfo, isAddesScript]);

    return (
        <Wrapper>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                {tourDataInfo && (
                    <MainWrapper>
                        <Box style={{ width: '100%', backgroundColor: '#F5F5F5' }}>
                            <HeroBlock tourDataInfo={tourDataInfo} isMobile={isMobile} />
                            <FormSection
                                timeNotEmpty={timeNotEmpty}
                                dateNotEmpty={dateNotEmpty}
                                dropOffNotEmpty={dropOffNotEmpty}
                                setDateNotEmpty={setDateNotEmpty}
                                setTimeNotEmpty={setTimeNotEmpty}
                                setDropOffNotEmpty={setDropOffNotEmpty}
                                setLocationNotEmpty={setLocationNotEmpty}
                                locationNotEmpty={locationNotEmpty}
                                isMobile={isMobile}
                                dropOffAutocomplete={dropOffAutocomplete}
                                pickupAutocomplete={pickupAutocomplete}
                                tourDataInfo={tourDataInfo}
                                allRoutes={allRoutes}
                                total={total}
                                setTotal={setTotal}
                                date={date}
                                setDate={setDate}
                                time={time}
                                setTime={setTime}
                                dropOffLocation={dropOffLocation}
                                setDropOffLocation={setDropOffLocation}
                                checkboxIsCheked={checkboxIsCheked}
                                setCheckboxIsCheked={setCheckboxIsCheked}
                                pickUpLocation={pickUpLocation}
                                setPickUpLocation={setPickUpLocation}
                            />
                            <AddedStopsSection
                                setMobileButtonsVisible={setMobileButtonsVisible}
                                isMobile={isMobile}
                                addClicked={addClicked}
                                addedStops={addedStopsArr}
                                addedStopsId={addedStopsId}
                                popularStopsId={popularStopsId}
                                popularStopsCount={popularStopsArr.length}
                                removeStation={removeStation}
                                total={total}
                                tourDefaultPrice={tourDataInfo.basic_price}
                            />
                            <AddStopSections
                                isMobile={isMobile}
                                stops={tourDataInfo.places}
                                addedStopsId={addedStopsId}
                                total={total}
                                tourDefaultPrice={tourDataInfo.basic_price}
                                addStation={addStation}
                                open={modalIsOpen}
                                handleCloseModal={handleCloseModal}
                                handleOpenModal={handleOpenModal}
                            />
                        </Box>
                        <TotalSection
                            setDate={setDate}
                            setDateNotEmpty={setDateNotEmpty}
                            setTimeNotEmpty={setTimeNotEmpty}
                            setDropOffNotEmpty={setDropOffNotEmpty}
                            setLocationNotEmpty={setLocationNotEmpty}
                            dropOffLocation={dropOffLocation}
                            totalMinutes={duration}
                            addedStops={addedStopsArr}
                            total={total}
                            setTotal={setTotal}
                            tourDefaultPrice={tourDataInfo.basic_price}
                            slug={tourDataInfo.tour.slug}
                            date={date}
                            time={time}
                            tourId={tourDataInfo.id}
                            currentRouteName={tourDataInfo.tour.name}
                            pickUpLocation={pickUpLocation}
                            checkboxIsCheked={checkboxIsCheked}
                        />
                        <TotalBlockButtons
                            setDate={setDate}
                            setTotal={setTotal}
                            mobileButtonsVisible={mobileButtonsVisible}
                            setDateNotEmpty={setDateNotEmpty}
                            setTimeNotEmpty={setTimeNotEmpty}
                            setDropOffNotEmpty={setDropOffNotEmpty}
                            setLocationNotEmpty={setLocationNotEmpty}
                            totalMinutes={duration}
                            dropOffLocation={dropOffLocation}
                            setClickAdd={setAddClicked}
                            addedStops={addedStopsArr}
                            addedStopsId={addedStopsId}
                            defaultStops={tourDataInfo.places}
                            total={total}
                            tourDefaultPrice={tourDataInfo.basic_price}
                            slug={tourDataInfo.tour.slug}
                            date={date}
                            time={time}
                            addStation={addStation}
                            tourId={tourDataInfo.id}
                            pickUpLocation={pickUpLocation}
                            checkboxIsCheked={checkboxIsCheked}
                            open={modalIsOpen}
                            handleOpenModal={handleOpenModal}
                            handleCloseModal={handleCloseModal}
                        />
                    </MainWrapper>
                )}
            </LocalizationProvider>
        </Wrapper>
    );
}

export default App;
